import { WorkRuleSetRule } from "@rhumbix/rmbx_work_rules"
import { AllExceptions, AnyException, EodAnswerFact, EodAnswersByEmployee, Thunk, TkSummary } from "../common/types"
import { _get } from "../api/util"
import { getEodAnswers } from "../api/post"
import { getFlagEnabled } from "../getFlagValue"
import { stableStringify } from "../common/ts-utils"

interface iToggleException {
    type: "TOGGLE_EXCEPTION"
    whichException: keyof AllExceptions
}

interface iSetExceptionValue {
    type: "SET_EXCEPTION_VALUE"
    exception: AnyException
}

interface iSetTkSummary {
    type: "SET_TK_SUMMARY"
    data: any
    queryString: string | null
}

interface iMergeTkSummary {
    type: "MERGE_TK_SUMMARY"
    data: TkSummary
    employeeIds: number[]
}

interface iSetEodAnswersByEmployee {
    type: "SET_EOD_ANSWERS_BY_EMPLOYEE"
    data: EodAnswersByEmployee
    queryString: string | null
}

interface iMergeEodAnswersByEmployee {
    type: "MERGE_EOD_ANSWERS_BY_EMPLOYEE"
    data: EodAnswersByEmployee
    employeeIds: number[]
}

interface iSetWorkRuleOverride {
    type: "SET_WORK_RULE_OVERRIDE"
    data: WorkRuleSetRule[]
}

interface iClearWorkRuleOverride {
    type: "CLEAR_WORK_RULE_OVERRIDE"
}

interface iFilterExceptions {
    type: "TOGGLE_FILTER_EXCEPTIONS"
}

export const setExceptionValue = (exception: AnyException): iSetExceptionValue => {
    return {
        type: "SET_EXCEPTION_VALUE",
        exception,
    }
}

export const toggleException = (whichException: keyof AllExceptions): iToggleException => {
    return {
        type: "TOGGLE_EXCEPTION",
        whichException,
    }
}

export const toggleFilterExceptions = (): iFilterExceptions => {
    return {
        type: "TOGGLE_FILTER_EXCEPTIONS",
    }
}

export const setTkSummary = (tkSummary: TkSummary, queryString: string | null = null): iSetTkSummary => {
    return {
        type: "SET_TK_SUMMARY",
        data: tkSummary,
        queryString,
    }
}

/**
 * Merge a per-employee timecard summary into the existing store summary. The listed
 * `employeeIds` are cleared before overlaying `tkSummary`, so an employee who no longer has
 * any rows (the endpoint omits employees with no data) doesn't retain stale rows.
 *
 * @param tkSummary The freshly fetched summary, keyed by employee ID.
 * @param employeeIds The employees that were requested (the authoritative set to refresh).
 */
export const mergeTkSummary = (tkSummary: TkSummary, employeeIds: number[]): iMergeTkSummary => {
    return {
        type: "MERGE_TK_SUMMARY",
        data: tkSummary,
        employeeIds,
    }
}

/**
 * Fetch the timecard summary and apply it to the store. A non-empty `employeeIds` always scopes
 * the request to those employees and merges the result, even with no baseline loaded yet; an
 * empty/omitted list fetches and replaces the full summary, deduped against `tkSummaryQueryString`
 * (via `stableStringify`, so key order doesn't matter) under `WA-9523-tk-summary-austerity`.
 * @param requestParams The tk_summary query params (date range, group_by, etc.), without `employee_id`.
 * @param employeeIds The employees to refresh, or null/empty to fetch the full summary.
 */
export const refreshTkSummary = (requestParams: Record<string, unknown>, employeeIds: number[] | null = null): Thunk<Promise<void>> => (dispatch, getState) => {
    const { tkSummaryQueryString } = getState().exceptions
    const isScoped = !!employeeIds && employeeIds.length > 0
    const params = isScoped ? { ...requestParams, employee_id: employeeIds } : requestParams
    const queryString = stableStringify(params)

    if (!isScoped && getFlagEnabled("WA-9523-tk-summary-austerity") && tkSummaryQueryString === queryString) {
        return Promise.resolve()
    }

    return _get("/api/v4/employees/tk_summary/", params)
        .then((response: TkSummary) => {
            if (isScoped) {
                dispatch(mergeTkSummary(response, employeeIds as number[]))
            } else {
                dispatch(setTkSummary(response, queryString))
            }
        })
        .catch((error: unknown) => {
            console.error("Error refreshing TK summary:", error)
        })
}

export const setEodAnswersByEmployee = (eodAnswersByEmployee: EodAnswersByEmployee, queryString: string | null = null): iSetEodAnswersByEmployee => {
    return {
        type: "SET_EOD_ANSWERS_BY_EMPLOYEE",
        data: eodAnswersByEmployee,
        queryString,
    }
}

/**
 * Merge a per-employee EoD answer set into the existing store. The listed `employeeIds` are
 * cleared before overlaying `eodAnswersByEmployee`, so an employee who no longer has any answers
 * for the requested range doesn't retain stale rows. Mirrors `mergeTkSummary`.
 *
 * @param eodAnswersByEmployee The freshly fetched answers, keyed by employee ID.
 * @param employeeIds The employees that were requested (the authoritative set to refresh).
 */
export const mergeEodAnswersByEmployee = (eodAnswersByEmployee: EodAnswersByEmployee, employeeIds: number[]): iMergeEodAnswersByEmployee => {
    return {
        type: "MERGE_EOD_ANSWERS_BY_EMPLOYEE",
        data: eodAnswersByEmployee,
        employeeIds,
    }
}

/**
 * Fetch EoD answers for a set of employees/date range and apply them to the store, grouped by
 * employee ID to match `TkSummary`'s shape. A non-empty `employeeIds` always scopes the request
 * to those employees and merges the result; an empty/omitted list fetches and replaces the full
 * set, deduped against `eodAnswersByEmployeeQueryString` (via `stableStringify`) so remounting
 * with the same scope doesn't refetch.
 *
 * Fetched once per view (see `ChecklistButton`'s `doEodAnswersFetch`) instead of being embedded
 * on every TK entry/start-stop row server-side -- this is the single query that backs
 * Fancy Find's EoD-answer categories across all leaf resource types (TK entries, shift extras,
 * shifts & breaks, absences), joined client-side by employee+date.
 *
 * @param employeeIds The employees to fetch/refresh, or null/empty for the full view.
 * @param startDate The requested range's start date, YYYY-MM-DD.
 * @param endDate The requested range's end date, YYYY-MM-DD.
 */
export const refreshEodAnswersByEmployee = (employeeIds: number[] | null, startDate: string, endDate: string): Thunk<Promise<void>> => (dispatch, getState) => {
    const { eodAnswersByEmployeeQueryString } = getState().exceptions
    const isScoped = !!employeeIds && employeeIds.length > 0
    const requestEmployeeIds = isScoped ? (employeeIds as number[]) : Object.keys(getState().entities.employees?.objects ?? {}).map(Number)
    const queryString = stableStringify({ employeeIds: requestEmployeeIds, startDate, endDate })

    if (!isScoped && eodAnswersByEmployeeQueryString === queryString) {
        return Promise.resolve()
    }
    if (requestEmployeeIds.length === 0) {
        return Promise.resolve()
    }

    return getEodAnswers(requestEmployeeIds, startDate, endDate)
        .then((response: { results: (EodAnswerFact & { employee_id: number })[] }) => {
            const eodAnswersByEmployee: EodAnswersByEmployee = {}
            response.results.forEach(({ employee_id, ...record }) => {
                const key = String(employee_id)
                if (!eodAnswersByEmployee[key]) eodAnswersByEmployee[key] = []
                eodAnswersByEmployee[key].push(record)
            })
            if (isScoped) {
                dispatch(mergeEodAnswersByEmployee(eodAnswersByEmployee, requestEmployeeIds))
            } else {
                dispatch(setEodAnswersByEmployee(eodAnswersByEmployee, queryString))
            }
        })
        .catch((error: unknown) => {
            console.error("Error refreshing EoD answers:", error)
        })
}

export const setWorkRuleOverride = (workRuleValues: WorkRuleSetRule[]): iSetWorkRuleOverride => {
    return {
        type: "SET_WORK_RULE_OVERRIDE",
        data: workRuleValues,
    }
}

export const clearWorkRuleOverride = (): iClearWorkRuleOverride => {
    return {
        type: "CLEAR_WORK_RULE_OVERRIDE",
    }
}

export type ExceptionAction =
    | iToggleException
    | iSetExceptionValue
    | iFilterExceptions
    | iSetTkSummary
    | iMergeTkSummary
    | iSetEodAnswersByEmployee
    | iMergeEodAnswersByEmployee
    | iSetWorkRuleOverride
    | iClearWorkRuleOverride
